import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { alignItems, borderRadius, size, space } from 'styled-system';
import type {
  AlignItemsProps,
  BorderRadiusProps,
  DisplayProps,
  SizeProps,
  SpaceProps,
} from 'styled-system';

import { Button } from 'common/components/Button';
import type { FlexProps } from 'common/components/Flex';
import { Flex } from 'common/components/Flex';
import Icon from 'common/components/Icon';
import { Text } from 'common/components/Text';
import { HeadlineH5 } from 'common/components/Typography';
import { useOpenDrawer } from 'common/hooks/drawer';
import { useLocalization } from 'common/providers/LocaleProvider';
import { cursor, whiteSpace } from 'common/theme/system-utilities';
import type { CursorProps, WhiteSpaceProps } from 'common/theme/system-utilities';

const INFO_ICON_SIZE = 24;

const BareButton = styled(Button)<
  SpaceProps & CursorProps & AlignItemsProps & WhiteSpaceProps & BorderRadiusProps
>`
  ${space};
  ${cursor};
  ${alignItems};
  ${whiteSpace};
  ${borderRadius};
`;

const bareButtonStyles: ComponentProps<typeof BareButton> = {
  kind: 'bare',
  role: 'button',
  p: 'sp_0',
  cursor: 'pointer',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  borderRadius: '100px',
};

const InfoIcon = styled(Icon)<SpaceProps & DisplayProps & SizeProps>`
  ${size};
`;

const infoIconStyles: ComponentProps<typeof InfoIcon> = {
  size: INFO_ICON_SIZE,
  name: 'info',
};

const RankContainer = styled.div<SpaceProps>`
  ${space};
`;

const rankContainerStyles: ComponentProps<typeof RankContainer> = {
  pt: 'sp_16',
  px: 'sp_20',
};

const bodyStyles: ComponentProps<typeof Text> = {
  fontSize: 'fs_14',
  lineHeight: 'lh_20',
  mt: 'sp_16',
};

const ButtonHitArea = styled(Flex)<FlexProps & SpaceProps & CursorProps>`
  ${cursor};
`;

const buttonHitAreaStyles: ComponentProps<typeof ButtonHitArea> = {
  p: 'sp_12',
  ml: '-8px',
  mr: '-12px',
  cursor: 'pointer',
};

export const RankingInfo = () => {
  const { t } = useLocalization();

  const onClick: React.MouseEventHandler = useOpenDrawer({
    title: t('h24_results_ranking_title'),
    content: (
      <RankContainer {...rankContainerStyles}>
        <HeadlineH5>{t('h24_results_ranking_body_title')}</HeadlineH5>
        <Text
          {...bodyStyles}
          dangerouslySetInnerHTML={{ __html: t('h24_results_ranking_details') }}
        />
      </RankContainer>
    ),
  });

  return (
    <ButtonHitArea onClick={onClick} {...buttonHitAreaStyles}>
      <BareButton {...bareButtonStyles} role="button" aria-label={t('h24_results_ranking_button')}>
        <InfoIcon {...infoIconStyles} dataTestId="search-ranking-icon" />
      </BareButton>
    </ButtonHitArea>
  );
};
